<template>
  <div class="item-to-role-container">
    <!-- 新增：展示当前查询时间（可选，提升体验） -->
    <div class="date-info" v-if="props.selectedDate">
      <span class="date-label">当前查询日期：</span>
      <span class="date-value">{{ props.selectedDate }}</span>
    </div>

    <!-- 1. 物品名称查询表单（原有内容） -->
    <div class="search-form">
      <el-input
        v-model="itemName"
        placeholder="请输入物品名称（如：屠龙刀、魔法盾）"
        class="form-input"
        clearable
        @keyup.enter="searchRoleByItem"
      />
      <el-button type="primary" size="small" @click="searchRoleByItem" :disabled="!itemName.trim()">
        查询角色
      </el-button>
    </div>

    <!-- 2. 下拉框（原有内容） -->
    <div class="role-selector">
      <el-form-item label="选择角色" class="role-label">
        <el-select
          v-model="selectedRoleName"
          placeholder="请选择角色查看物品详情"
          class="role-select"
          clearable
          @change="loadRoleItemDetail"
        >
          <el-option v-if="roleList.length === 0" label="暂无角色数据" value="" disabled />
          <el-option
            v-for="roleName in roleList"
            :key="roleName"
            :label="roleName"
            :value="roleName"
          />
        </el-select>
      </el-form-item>
    </div>

    <!-- 3. 物品详情表格（原有内容） -->
    <div class="item-detail-wrapper">
      <table class="item-detail-table">
        <thead>
          <tr>
            <th>物品名称</th>
            <th>物品来源</th>
            <th>物品介绍</th>
            <th>物品数量</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="roleItemDetail.length === 0">
            <td colspan="4" class="empty-cell">未找到匹配的数据</td>
          </tr>
          <tr v-for="(item, index) in roleItemDetail" :key="index">
            <td>{{ item.itemname }}</td>
            <td>{{ item.dec || '无来源' }}</td>
            <td>{{ item.username || '无介绍' }}</td>
            <td>{{ item.count }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { LOG_API } from '@/utils/apiBase';
import { ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import axios from 'axios';

// 1. 核心修改：在Props中新增selectedDate参数（必加）
const props = defineProps({
  serverId: {
    type: String,
    required: true
  },
  selectedDate: { // 新增：接收父组件的时间参数
    type: String,
    default: '' // 默认为空，表示查询全部时间
  }
});

// 响应式数据（原有内容不变）
const itemName = ref('');
const roleList = ref([]);
const selectedRoleName = ref('');
const roleItemDetail = ref([]);

// 2. 优化：查询角色时也传入时间参数（可选，根据接口需求）
const searchRoleByItem = async () => {
  selectedRoleName.value = '';
  roleItemDetail.value = [];
  
  if (!itemName.value.trim()) {
    ElMessage.warning('请输入物品名称后再查询');
    return;
  }

  try {
    const res = await axios.get(`${LOG_API}/api/drop/GetuserNameByThing`, {
      params: { 
        serverId: props.serverId, 
        itemName: itemName.value.trim(),
        time: props.selectedDate ? props.selectedDate : '所有' // 新增：传入时间参数
      },
      timeout: 10000
    });
    console.log('接口：GetuserNameByThing ',props.serverId,itemName.value.trim(),props.selectedDate ? props.selectedDate : '所有');
    const responseData = res.data;
    if (responseData.code === 200) {
      roleList.value = responseData.data || [];
      if (roleList.value.length === 0) {
        ElMessage.info('未查询到拥有该物品的角色');
      }
    } else {
      ElMessage.warning(responseData.msg || '查询结果异常');
      roleList.value = [];
    }
  } catch (err) {
    console.error('查询角色失败:', err);
    roleList.value = [];
    ElMessage.error('查询角色失败，请稍后重试');
  }
};

// 3. 原有方法：已使用props.selectedDate，无需修改（仅确认）
const loadRoleItemDetail = async () => {
  if (!selectedRoleName.value) {
    roleItemDetail.value = [];
    return;
  }

  try {
    const res = await axios.get(`${LOG_API}/api/drop/GetAllItemsByuserName`, {
      params: { 
        serverId: props.serverId, 
        userName: selectedRoleName.value, 
        itemName: itemName.value.trim(),
        time: props.selectedDate ? props.selectedDate : '所有' // 已存在，无需修改
      },
      timeout: 10000
    });
    console.log('接口：GetAllItemsByuserName ',props.serverId,selectedRoleName.value,itemName.value.trim(),props.selectedDate ? props.selectedDate : '所有');
    console.log('物品详情返回数据：', res.data);

    const responseData = res.data;
    if (responseData.code === 200) {
      roleItemDetail.value = responseData.data || [];
    } else {
      ElMessage.warning(responseData.msg || '查询结果异常');
      roleItemDetail.value = [];
    }
  } catch (err) {
    console.error('查询物品详情失败:', err);
    roleItemDetail.value = [];
    ElMessage.error('查询物品详情失败，请稍后重试');
  }
};

// 4. 核心修改：监听时间变化，自动刷新数据（必加）
watch(
  () => props.selectedDate, // 监听父组件传递的时间参数
  (newDate, oldDate) => {
    if (newDate !== oldDate) {
      ElMessage.info(`查询日期已切换为：${newDate || '全部日期'}`);
      // 若已有查询条件，自动重新查询
      if (itemName.value.trim()) {
        searchRoleByItem(); // 重新查询角色列表
        // 若已选中角色，重新查询物品详情
        if (selectedRoleName.value) {
          setTimeout(() => loadRoleItemDetail(), 300); // 延迟避免请求冲突
        }
      }
    }
  },
  { immediate: false }
);

</script>

<style scoped>
/* 新增：日期信息展示样式 */
.date-info {
  margin-bottom: 12px;
  padding: 6px 10px;
  background-color: #2d2d44;
  border-radius: 4px;
  display: inline-block;
}
.date-label {
  color: #ccc;
  font-size: 12px;
}
.date-value {
  color: #3a7bd5;
  font-weight: 500;
  font-size: 12px;
  margin-left: 4px;
}

/* 原有样式不变 */
.item-to-role-container {
  padding: 16px;
  color: #fff;
  background-color: #1e293b;
  border-radius: 8px;
}
.search-form {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  align-items: center;
}
.form-input {
  flex: 1;
  min-width: 200px;
  --el-input-bg-color: #2d2d44;
  --el-input-border-color: #3d3d5c;
  --el-input-text-color: #fff;
  --el-input-placeholder-color: #888;
}
.role-selector {
  margin-bottom: 24px;
  display: block;
}
.role-label {
  --el-form-item-label-color: #e0e0e0;
  margin-bottom: 0 !important;
}
.role-select {
  width: 300px;
  --el-select-bg-color: #2d2d44;
  --el-select-border-color: #3d3d5c;
  --el-select-text-color: #fff;
  --el-select-placeholder-color: #888;
}
.item-detail-wrapper {
  width: 100%;
}
.item-detail-table {
  width: 100%;
  border-collapse: collapse;
  background-color: #2d2d44;
  border-radius: 4px;
  overflow: hidden;
}
.item-detail-table th {
  background-color: #3d3d5c;
  padding: 8px 12px;
  text-align: left;
  color: #ccc;
  border-bottom: 1px solid #3d3d5c;
}
.item-detail-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #3d3d5c;
  color: #e0e0e0;
}
.empty-cell {
  text-align: center;
  color: #888;
  padding: 20px 0;
}
</style>